import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import weatherStationService, {
  type WeatherStation,
  type WeatherStationValidatePreview,
} from '../../services/weatherStations';
import orchardService from '../../services/orchard';
import type { Farm, FarmsResponse } from '../../types/orchard';
import {
  Plus, Trash2, Radio, Wifi, WifiOff, Loader2, CheckCircle2, AlertCircle,
  Pencil, RefreshCw, PauseCircle,
} from 'lucide-react';
import '../../styles/GlobalPageStyles.css';

function formatAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface WeatherStationSetupProps {
  openPairOnMount?: boolean;
}

export default function WeatherStationSetup({ openPairOnMount = false }: WeatherStationSetupProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shouldAutoOpen = openPairOnMount || searchParams.get('pair') === '1';

  const [stations, setStations] = useState<WeatherStation[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pairing modal state
  const [showPair, setShowPair] = useState(shouldAutoOpen);
  const [applicationKey, setApplicationKey] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [devices, setDevices] = useState<WeatherStationValidatePreview[] | null>(null);
  const [selectedMac, setSelectedMac] = useState<string>('');
  const [selectedName, setSelectedName] = useState('');
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Edit modal state
  const [editingStation, setEditingStation] = useState<WeatherStation | null>(null);
  const [editName, setEditName] = useState('');
  const [editFarmId, setEditFarmId] = useState<number | null>(null);
  const [editActive, setEditActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editReplaceKeys, setEditReplaceKeys] = useState(false);
  const [editAppKey, setEditAppKey] = useState('');
  const [editApiKey, setEditApiKey] = useState('');

  // Backfill state (per-station)
  const [backfillingId, setBackfillingId] = useState<number | null>(null);
  const [backfillMessage, setBackfillMessage] = useState<{ id: number; text: string; ok: boolean } | null>(null);

  async function refresh() {
    try {
      setLoading(true);
      setError(null);
      const res = await weatherStationService.list();
      if (res?.success && res.data) setStations(res.data);
      else setError('Failed to load stations');
    } catch (e) {
      setError((e as Error).message || 'Failed to load stations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // Load farms for the farm picker
    orchardService.getFarms(1, 200).then((res) => {
      if (res?.success && res.data) {
        const list = Array.isArray(res.data)
          ? (res.data as Farm[])
          : ((res.data as FarmsResponse).farms ?? []);
        setFarms(list);
      }
    }).catch(() => { /* ignore */ });
  }, []);

  function resetPairForm() {
    setApplicationKey('');
    setApiKey('');
    setDevices(null);
    setSelectedMac('');
    setSelectedName('');
    setSelectedFarmId(null);
    setValidationError(null);
    setSaveError(null);
  }

  async function handleValidate() {
    setValidating(true);
    setValidationError(null);
    setDevices(null);
    try {
      const res = await weatherStationService.validateKey(applicationKey.trim(), apiKey.trim());
      if (res?.success && res.data) {
        setDevices(res.data);
        if (res.data.length === 1) {
          setSelectedMac(res.data[0].mac_address);
          setSelectedName(res.data[0].name ?? '');
        }
        if (res.data.length === 0) {
          setValidationError('The keys are valid but no stations are visible on that Ambient Weather account.');
        }
      } else {
        setValidationError(res?.message || 'Validation failed');
      }
    } catch (e) {
      setValidationError((e as Error).message || 'Validation failed');
    } finally {
      setValidating(false);
    }
  }

  async function handleSave() {
    if (!selectedMac || !selectedName.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await weatherStationService.create({
        mac_address: selectedMac,
        name: selectedName.trim(),
        application_key: applicationKey.trim(),
        api_key: apiKey.trim(),
        farm_id: selectedFarmId,
      });
      if (res?.success) {
        setShowPair(false);
        resetPairForm();
        await refresh();
      } else {
        setSaveError(res?.message || 'Failed to save');
      }
    } catch (e) {
      setSaveError((e as Error).message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number, name: string) {
    if (!confirm(`Remove station "${name}"? Historical observations will be retained but polling will stop.`)) return;
    try {
      const res = await weatherStationService.delete(id);
      if (res?.success) await refresh();
    } catch { /* ignore */ }
  }

  function openEdit(s: WeatherStation) {
    setEditingStation(s);
    setEditName(s.name);
    setEditFarmId(s.farm_id);
    setEditActive(s.active);
    setEditError(null);
    setEditReplaceKeys(false);
    setEditAppKey('');
    setEditApiKey('');
  }

  function closeEdit() {
    setEditingStation(null);
    setEditError(null);
    setEditReplaceKeys(false);
    setEditAppKey('');
    setEditApiKey('');
  }

  async function handleEditSave() {
    if (!editingStation) return;
    if (!editName.trim()) {
      setEditError('Display name is required');
      return;
    }
    if (editReplaceKeys && (!editAppKey.trim() || !editApiKey.trim())) {
      setEditError('Both Application key and API key are required when replacing keys');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const payload: Parameters<typeof weatherStationService.update>[1] = {
        name: editName.trim(),
        farm_id: editFarmId,
        active: editActive,
      };
      if (editReplaceKeys) {
        payload.application_key = editAppKey.trim();
        payload.api_key = editApiKey.trim();
      }
      const res = await weatherStationService.update(editingStation.id, payload);
      if (res?.success) {
        closeEdit();
        await refresh();
      } else {
        setEditError(res?.message || 'Failed to save');
      }
    } catch (e) {
      setEditError((e as Error).message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleBackfill(id: number) {
    setBackfillingId(id);
    setBackfillMessage(null);
    try {
      const res = await weatherStationService.triggerBackfill(id);
      if (res?.success) {
        setBackfillMessage({ id, text: 'Backfill started — historical data will appear over the next few minutes.', ok: true });
      } else {
        setBackfillMessage({ id, text: res?.message || 'Backfill failed to start.', ok: false });
      }
    } catch (e) {
      setBackfillMessage({ id, text: (e as Error).message || 'Backfill failed to start.', ok: false });
    } finally {
      setBackfillingId(null);
      setTimeout(() => setBackfillMessage((m) => (m && m.id === id ? null : m)), 6000);
    }
  }

  return (
    <>
      <PageHeader title="Weather Stations" subtitle="Pair your Ambient Weather stations to farms" />
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="text-sm text-neutral-500">
            {stations.length} station{stations.length === 1 ? '' : 's'} paired
          </div>
          <Button onClick={() => { resetPairForm(); setShowPair(true); }}>
            <Plus className="h-4 w-4 mr-1" /> Pair station
          </Button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && !loading && (
          <Card><CardContent className="py-4 text-rose-600 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {error}</CardContent></Card>
        )}

        {/* Station list */}
        <div className="grid gap-3">
          {stations.map((s) => (
            <Card key={s.id}>
              <CardContent className="py-4 flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                  <Radio className="h-6 w-6 text-sky-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/weather/${s.id}`)}
                      className="font-semibold text-neutral-900 hover:text-sky-600 truncate"
                    >
                      {s.name}
                    </button>
                    <span className="text-xs font-mono text-neutral-400">{s.mac_address}</span>
                  </div>
                  <div className="text-sm text-neutral-500 truncate">
                    {s.farm_name || 'Not linked to a farm'}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    {s.last_seen_at
                      ? <span className="flex items-center gap-1 text-green-700"><Wifi className="h-3 w-3" /> {formatAgo(s.last_seen_at)}</span>
                      : <span className="flex items-center gap-1 text-neutral-500"><WifiOff className="h-3 w-3" /> never</span>}
                    {!s.active && (
                      <span className="flex items-center gap-1 text-amber-700"><PauseCircle className="h-3 w-3" /> paused</span>
                    )}
                    {s.last_poll_error && <span className="text-rose-600 truncate">· {s.last_poll_error}</span>}
                  </div>
                  {backfillMessage && backfillMessage.id === s.id && (
                    <div className={`mt-2 text-xs ${backfillMessage.ok ? 'text-green-700' : 'text-rose-600'}`}>
                      {backfillMessage.text}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleBackfill(s.id)}
                    disabled={backfillingId === s.id}
                    className="p-2 rounded-lg text-sky-600 hover:bg-sky-50 disabled:opacity-50"
                    title="Backfill last 30 days"
                  >
                    {backfillingId === s.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <RefreshCw className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => openEdit(s)}
                    className="p-2 rounded-lg text-neutral-600 hover:bg-neutral-100"
                    title="Edit station"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(s.id, s.name)}
                    className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"
                    title="Remove station"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!loading && !error && stations.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-neutral-500">
                No weather stations paired yet. Click <b>Pair station</b> to add one.
              </CardContent>
            </Card>
          )}
        </div>

        {/* Pair modal */}
        {showPair && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
                <h2 className="text-lg font-bold">Pair a weather station</h2>
                <button onClick={() => setShowPair(false)} className="text-neutral-400 hover:text-neutral-700">✕</button>
              </div>

              <div className="p-6 space-y-4">
                {/* Step 1: Both keys */}
                <div className="space-y-3">
                  <div className="rounded-lg bg-sky-50 text-sky-900 text-xs p-3 leading-relaxed">
                    Log into your Ambient Weather account at{' '}
                    <a
                      href="https://ambientweather.net/account"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      ambientweather.net/account
                    </a>
                    {' '}and open the <b>API Keys</b> tab. You'll need both an
                    <b> Application Key</b> and an <b>API Key</b> (create one of each if you don't
                    have them yet). Both are stored encrypted and never shown again.
                  </div>

                  <Input
                    label="Application key"
                    placeholder="applicationKey"
                    value={applicationKey}
                    onChange={(e) => setApplicationKey(e.target.value)}
                  />
                  <Input
                    label="API key"
                    placeholder="apiKey"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                  />

                  <div className="mt-3 flex gap-2">
                    <Button
                      onClick={handleValidate}
                      disabled={!applicationKey.trim() || !apiKey.trim() || validating}
                    >
                      {validating && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                      {devices ? 'Revalidate' : 'Validate keys'}
                    </Button>
                  </div>
                  {validationError && (
                    <div className="mt-2 text-sm text-rose-600 flex items-start gap-1">
                      <AlertCircle className="h-4 w-4 mt-0.5" /> {validationError}
                    </div>
                  )}
                </div>

                {/* Step 2: Pick station */}
                {devices && devices.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-sm text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" /> Found {devices.length} station{devices.length === 1 ? '' : 's'}
                    </div>
                    <label className="block text-sm font-semibold text-neutral-700">Station</label>
                    <select
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm"
                      value={selectedMac}
                      onChange={(e) => {
                        setSelectedMac(e.target.value);
                        const d = devices.find((dd) => dd.mac_address === e.target.value);
                        if (d && !selectedName) setSelectedName(d.name ?? '');
                      }}
                    >
                      <option value="">— choose —</option>
                      {devices.map((d) => (
                        <option key={d.mac_address} value={d.mac_address}>
                          {d.name || d.mac_address} — {d.mac_address}
                        </option>
                      ))}
                    </select>

                    {selectedMac && (
                      <>
                        <Input
                          label="Display name"
                          placeholder="e.g. North Orchard Weather"
                          value={selectedName}
                          onChange={(e) => setSelectedName(e.target.value)}
                        />
                        <div>
                          <label className="block text-sm font-semibold text-neutral-700 mb-2">Farm (optional)</label>
                          <select
                            className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm"
                            value={selectedFarmId ?? ''}
                            onChange={(e) => setSelectedFarmId(e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value="">— not linked —</option>
                            {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                          </select>
                          <p className="text-xs text-neutral-500 mt-1">
                            Linking to a farm enables on-farm weather actuals for Spray, Farm Jobs, and rain-skip for Irrigation.
                          </p>
                        </div>

                        {saveError && (
                          <div className="text-sm text-rose-600 flex items-start gap-1">
                            <AlertCircle className="h-4 w-4 mt-0.5" /> {saveError}
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          <Button onClick={handleSave} disabled={!selectedName.trim() || saving}>
                            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                            Pair station
                          </Button>
                          <Button variant="ghost" onClick={() => setShowPair(false)}>Cancel</Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit modal */}
        {editingStation && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-neutral-100 flex items-center justify-between">
                <h2 className="text-lg font-bold">Edit weather station</h2>
                <button onClick={closeEdit} className="text-neutral-400 hover:text-neutral-700">✕</button>
              </div>

              <div className="p-6 space-y-4">
                <div className="text-xs text-neutral-500 font-mono">{editingStation.mac_address}</div>

                <Input
                  label="Display name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-2">Farm</label>
                  <select
                    className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm"
                    value={editFarmId ?? ''}
                    onChange={(e) => setEditFarmId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">— not linked —</option>
                    {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <p className="text-xs text-neutral-500 mt-1">
                    Linking to a farm enables on-farm weather actuals for Spray, Farm Jobs, and rain-skip for Irrigation.
                  </p>
                </div>

                <label className="flex items-center gap-3 p-3 rounded-lg border border-neutral-200 cursor-pointer hover:bg-neutral-50">
                  <input
                    type="checkbox"
                    checked={editActive}
                    onChange={(e) => setEditActive(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <div>
                    <div className="text-sm font-semibold text-neutral-800">Polling active</div>
                    <div className="text-xs text-neutral-500">
                      When off, the station stays paired but stops fetching new observations every 5 minutes.
                    </div>
                  </div>
                </label>

                <div className="rounded-lg border border-neutral-200">
                  <div className="flex items-center justify-between p-3">
                    <div>
                      <div className="text-sm font-semibold text-neutral-800">API keys</div>
                      <div className="text-xs text-neutral-500">
                        Replace the Ambient Weather keys without re-pairing. History is kept.
                      </div>
                    </div>
                    {!editReplaceKeys ? (
                      <Button variant="ghost" onClick={() => setEditReplaceKeys(true)}>
                        Replace keys
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setEditReplaceKeys(false);
                          setEditAppKey('');
                          setEditApiKey('');
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                  {editReplaceKeys && (
                    <div className="border-t border-neutral-100 p-3 space-y-3">
                      <div className="rounded-lg bg-sky-50 text-sky-900 text-xs p-3 leading-relaxed">
                        Paste fresh keys from{' '}
                        <a
                          href="https://ambientweather.net/account"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline font-semibold"
                        >
                          ambientweather.net/account
                        </a>
                        . The new keys must belong to the account that owns this station —
                        we'll verify the station's MAC is visible before saving.
                      </div>
                      <Input
                        label="New Application key"
                        placeholder="applicationKey"
                        value={editAppKey}
                        onChange={(e) => setEditAppKey(e.target.value)}
                      />
                      <Input
                        label="New API key"
                        placeholder="apiKey"
                        value={editApiKey}
                        onChange={(e) => setEditApiKey(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                {editError && (
                  <div className="text-sm text-rose-600 flex items-start gap-1">
                    <AlertCircle className="h-4 w-4 mt-0.5" /> {editError}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button onClick={handleEditSave} disabled={!editName.trim() || editSaving}>
                    {editSaving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                    Save changes
                  </Button>
                  <Button variant="ghost" onClick={closeEdit}>Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
